import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";

// Region definitions
export const REGIONS = {
  UK: {
    code: "UK",
    name: "United Kingdom",
    flagCode: "GB",       // ISO code used by the <Flag /> renderer
    currency: "GBP",
    currencySymbol: "£",
    companyLookupApi: "companies-house",
    mondayBoardId: "1895143857",
    docupilotTemplates: {
      ftla: "103132",
      debenture: "103134",
      personalGuarantee: "103130",
      pgCrossCompany: "103133"
    }
  },
  AUS: {
    code: "AUS",
    name: "Australia",
    flagCode: "AU",
    currency: "AUD",
    currencySymbol: "$",
    companyLookupApi: "asic",
    mondayBoardId: "5088671317",
    docupilotTemplates: {
      ftla: "103526"
    }
  },
  NZ: {
    code: "NZ",
    name: "New Zealand",
    flagCode: "NZ",
    currency: "NZD",
    currencySymbol: "$",
    // Roadmap region — visible everywhere but greyed out + non-switchable.
    // Removing this flag is all that's needed to enable NZ in the future.
    comingSoon: true,
  },
};

const RegionContext = createContext(null);

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error("useRegion must be used within RegionProvider");
  }
  return context;
};

// Map a team string like "Credit-[UK]" or "Collections-[AU]" to a region code.
// Cross-functional teams (Finance&Ops, Admin) → null (no auto-default).
const regionFromTeam = (team) => {
  if (!team) return null;
  if (team.includes("[UK]")) return "UK";
  if (team.includes("[AU]")) return "AUS";
  return null;
};

export const RegionProvider = ({ children }) => {
  const { user } = useAuth();
  const allowedRegions = useMemo(() => {
    // Every code region the user is allowed to work in. Fallback to all
    // non-comingSoon regions when auth isn't loaded yet, so nothing flickers.
    const grant = Array.isArray(user?.regions) && user.regions.length
      ? user.regions.map(r => (r === "AU" ? "AUS" : r))
      : null;
    return Object.keys(REGIONS).filter(code => {
      if (REGIONS[code].comingSoon) return false;
      if (!grant) return true;
      return grant.includes(code);
    });
  }, [user?.regions]);

  const [currentRegion, setCurrentRegion] = useState(() => {
    // Load from localStorage or default to UK. User-team default applied below.
    const saved = localStorage.getItem("sf_region");
    return saved && REGIONS[saved] ? saved : "UK";
  });

  // When a user logs in and we haven't yet seen them, default the region
  // to match their team. The user can still flip via the region switcher
  // (we persist their override in localStorage afterwards).
  useEffect(() => {
    if (!user) return;
    const explicit = localStorage.getItem("sf_region_user_override");
    if (explicit === "true") return;        // user already chose manually
    const auto = regionFromTeam(user.team);
    if (auto && REGIONS[auto] && auto !== currentRegion) {
      setCurrentRegion(auto);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.team]);

  // If the current region is NOT in the user's grant, snap to the first
  // allowed one (or, if none, leave as-is so the app doesn't blank out).
  useEffect(() => {
    if (!user || !allowedRegions.length) return;
    if (!allowedRegions.includes(currentRegion)) {
      setCurrentRegion(allowedRegions[0]);
    }
  }, [allowedRegions, currentRegion, user]);

  // Save region to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("sf_region", currentRegion);
  }, [currentRegion]);

  const switchRegion = (regionCode) => {
    const target = REGIONS[regionCode];
    if (!target) return false;
    if (target.comingSoon) return false;       // disabled roadmap regions
    if (!allowedRegions.includes(regionCode)) return false;  // not granted
    setCurrentRegion(regionCode);
    localStorage.setItem("sf_region_user_override", "true");
    return true;
  };

  const getRegionConfig = () => REGIONS[currentRegion];

  const isUK = () => currentRegion === "UK";
  const isAUS = () => currentRegion === "AUS";

  const value = {
    currentRegion,
    regionConfig: REGIONS[currentRegion],
    regions: REGIONS,
    allowedRegions,
    switchRegion,
    getRegionConfig,
    isUK,
    isAUS
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
};
